import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useSceneSeatPermission } from "@components/table/members/assign-seats/use-scene-seat-permission";
import { useCompanyId } from "@context-providers/company-id-provider";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useToast } from "@flat-ui";
import { setOne } from "@store/members/members-slice";
import { useAppDispatch } from "@store/store-helper";
import { nounPluralize } from "@utils/data-display";
import { sentryCaptureError } from "@utils/sentry-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName } from "@utils/user-utils";

/**
 * The workspace role that people outside the workspace join it with to be given a seat, shown as
 * "No workspace role": their access comes from the project role they are invited with.
 */
const NO_WORKSPACE_ROLE = "MEMBER";

/** A member to give a seat, along with the key that a failure for them is reported with */

/** How giving the seats went */

/**
 * Logs an error, since it is listed with the people it failed for rather than shown in an error
 * toast that would log it
 */
function logError(error) {
  sentryCaptureError({
    error,
    title: "Cannot give a Sphere XG seat"
  });
}

/**
 * Returns how to give several people a Sphere XG seat at once, with the requests, the refresh of
 * the seat usage, the tracking and the toast about it
 *
 * @param location Where the seats are given from, as tracked
 */
export function useGiveXgSeats(location) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    openToast
  } = useToast();
  const {
    setXgSeatPermission,
    refreshSeatUsage
  } = useSceneSeatPermission();

  /**
   * Invites the people outside the workspace to it, as only its members can hold a seat
   *
   * @returns the members they became, and the people that could not join
   */
  async function joinWorkspace(emails) {
    if (!emails.length) {
      return {
        joined: [],
        failures: []
      };
    }
    try {
      const {
        data,
        errorData
      } = await faroCoreApiClient.company.addMembersToCompany({
        companyId,
        payload: {
          assignments: [{
            identities: emails,
            role: NO_WORKSPACE_ROLE
          }]
        }
      });
      const failedByEmail = new Map(errorData?.details.failedInvitations.map(({
        identity,
        message
      }) => [identity.toLowerCase(), message]));
      const joined = [];
      const failures = [];
      for (const email_0 of emails) {
        const key = email_0.toLowerCase();
        const member = data.members.find(newMember => newMember.email.toLowerCase() === key);
        if (member && !failedByEmail.has(key)) {
          // Known as the pending member they are now, e.g. when the invite is retried after the
          // project invite failed, so that they do not join the workspace twice. The store holds
          // the members with the role assigned to them directly, which is the one they joined with.
          dispatch(setOne({
            ...member,
            userRole: NO_WORKSPACE_ROLE
          }));
          joined.push({
            key,
            member
          });
        } else {
          failures.push({
            key,
            label: email_0,
            reason: failedByEmail.get(key) ?? "Cannot invite them to the workspace"
          });
        }
      }
      return {
        joined,
        failures
      };
    } catch (error) {
      // One request failed for all of them, which is logged once
      logError(error);
      const reason = getErrorDisplayMarkup(error);
      return {
        joined: [],
        failures: emails.map(email => ({
          key: email.toLowerCase(),
          label: email,
          reason
        }))
      };
    }
  }
  async function giveSeats({
    members,
    emails: emails_0
  }) {
    const {
      joined: joined_0,
      failures: failures_0
    } = await joinWorkspace(emails_0);
    const membersToSeat = [...members.map(member_0 => ({
      key: member_0.identity,
      member: member_0
    })), ...joined_0];

    // Every request is waited for, so that the seat usage is refreshed once all of them are done
    const results = await Promise.allSettled(membersToSeat.map(({
      member: member_1
    }) => setXgSeatPermission(member_1)));
    refreshSeatUsage();
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const {
          key: key_0,
          member: member_2
        } = membersToSeat[index];
        logError(result.reason);
        failures_0.push({
          key: key_0,
          label: getPrettyName(member_2),
          reason: getErrorDisplayMarkup(result.reason)
        });
      }
    });

    // Only the seats that were actually given are tracked
    const numberOfSeatsGiven = results.filter(({
      status
    }) => status === "fulfilled").length;
    if (numberOfSeatsGiven) {
      trackEvent({
        name: WorkspaceEvents.assignXgSeat,
        props: {
          location,
          numberOfMembers: numberOfSeatsGiven
        }
      });
    }

    // Told on its own, since the seats stay assigned even when the invite fails afterwards. The
    // failures are listed along those of the invite instead.
    if (!failures_0.length && membersToSeat.length) {
      openToast({
        title: "Sphere XG seats assigned",
        message: `${nounPluralize({
          counter: membersToSeat.length,
          word: "member"
        })} now ${membersToSeat.length === 1 ? "holds" : "hold"} a Sphere XG seat.`
      });
    }
    return {
      failures: failures_0,
      numberOfSeatsGiven
    };
  }
  return {
    giveSeats
  };
}